import { useModel as _useModel, mergeModels as _mergeModels, defineComponent as _defineComponent } from 'vue';
import { unref as _unref, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment, createTextVNode as _createTextVNode, createVNode as _createVNode, withCtx as _withCtx, createBlock as _createBlock } from "vue";
const _hoisted_1 = { class: "flex flex-col items-center gap-2" };
const _hoisted_2 = { class: "flex flex-col items-center gap-0.5 text-center" };
const _hoisted_3 = ["id"];
const _hoisted_4 = ["id"];
const _hoisted_5 = { class: "flex flex-col gap-5" };
const _hoisted_6 = {
    key: 0,
    class: "text-[length:var(--size-l)] text-(--text-text-body) text-center leading-[var(--line-height-xxl)]"
};
const _hoisted_7 = { class: "text-[length:var(--size-l)] text-(--text-text-body)" };
const _hoisted_8 = { class: "flex flex-col gap-2" };
const _hoisted_9 = {
    key: 0,
    class: "flex items-start gap-2 text-[length:var(--size-m)] text-(--text-text-body)"
};
const _hoisted_10 = {
    key: 2,
    role: "alert",
    class: "rounded-lg bg-(--background-background-error) border border-(--border-border-error-subtle) px-4 py-3 text-sm text-(--text-text-error) flex items-center gap-2"
};
import { computed, nextTick, ref, useId, watch } from "vue";
import { useI18n } from "vue-i18n";
import { Alert, Button, Modal } from "@eudonet/opal";
export default /*@__PURE__*/ _defineComponent({
    __name: 'DeleteContactModal',
    props: /*@__PURE__*/ _mergeModels({
        preview: {},
        contactName: {},
        stepSubtitle: { default: null },
        householdNewPrincipalName: { default: null },
        submitting: { type: Boolean, default: false },
        serverError: { default: null }
    }, {
        "modelValue": { type: Boolean, ...{ default: false } },
        "modelModifiers": {},
    }),
    emits: /*@__PURE__*/ _mergeModels(["confirm", "cancel"], ["update:modelValue"]),
    setup(__props, { emit: __emit }) {
        /**
         * Path A / Path B contact-deletion confirmation (OP-7215). Path C (critical-link blocking) is
         * out of scope — the deletion preview never reports one, so this modal only ever renders the
         * "no links" (Path A) or "non-critical links" (Path B) content. Path D (permission) and the
         * shared-email designation wizard are handled upstream in ContactDetail.vue before this modal
         * ever opens; `stepSubtitle` lets the caller relabel this as "Étape 2 sur 2" when it follows
         * that wizard.
         */
        const props = __props;
        const open = _useModel(__props, "modelValue");
        const emit = __emit;
        const { t } = useI18n();
        const titleId = useId();
        const subtitleId = useId();
        const cancelButtonRef = ref(null);
        // Destructive-action safety (OP-6527 AC): focus lands on "Annuler", never the confirm button.
        // nextTick() wins the race against Modal's own rAF-deferred autofocus fallback (see useFocusTrap.ts).
        watch(open, async (isOpen) => {
            if (!isOpen)
                return;
            await nextTick();
            cancelButtonRef.value?.$el?.focus();
        });
        const hasLinks = computed(() => props.preview?.hasNonCriticalLinks ?? false);
        const subtitle = computed(() => {
            if (props.stepSubtitle)
                return props.stepSubtitle;
            return hasLinks.value
                ? t("contact.detail.deleteContact.subtitleWithLinks")
                : t("contact.detail.deleteContact.subtitleNoLinks");
        });
        const organisationNames = computed(() => props.preview?.organisations.map((o) => o.organisationName).join(", ") ?? "");
        const RELATION_TYPE_KEYS = {
            spouse: "contact.detail.deleteContact.relationTypes.spouse",
            parent: "contact.detail.deleteContact.relationTypes.parent",
            child: "contact.detail.deleteContact.relationTypes.child",
            sibling: "contact.detail.deleteContact.relationTypes.sibling",
            colleague: "contact.detail.deleteContact.relationTypes.colleague",
            friend: "contact.detail.deleteContact.relationTypes.friend",
            other: "contact.detail.deleteContact.relationTypes.other",
        };
        function relationTypeLabel(raw) {
            const key = RELATION_TYPE_KEYS[raw.toLowerCase()];
            if (!key)
                return raw;
            const translated = t(key);
            return translated === key ? raw : translated;
        }
        return (_ctx, _cache) => {
            return (_openBlock(), _createBlock(_unref(Modal), {
                modelValue: open.value,
                "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event) => ((open).value = $event)),
                size: hasLinks.value ? 'md' : 'sm',
                "header-position": "center",
                "footer-position": "split",
                "show-close-button": !__props.submitting,
                dismissable: !__props.submitting,
                "close-on-escape": !__props.submitting,
                "show-header-border": false,
                "show-footer-border": false,
                "aria-labelledby": _unref(titleId),
                "aria-describedby": _unref(subtitleId)
            }, {
                header: _withCtx(() => [
                    _createElementVNode("div", _hoisted_1, [
                        _cache[3] || (_cache[3] = _createElementVNode("div", { class: "flex items-center justify-center w-12 h-12 rounded-lg border border-(--border-border-base-soft) bg-white" }, [
                            _createElementVNode("i", {
                                class: "ri-delete-bin-6-line text-2xl text-(--text-text-black-default)",
                                "aria-hidden": "true"
                            })
                        ], -1 /* CACHED */)),
                        _createElementVNode("div", _hoisted_2, [
                            _createElementVNode("h2", {
                                id: _unref(titleId),
                                class: "text-[length:var(--size-xxl)] font-[var(--weight-bold)]"
                            }, _toDisplayString(_ctx.$t("contact.detail.deleteContact.title", { name: __props.contactName })), 9 /* TEXT, PROPS */, _hoisted_3),
                            _createElementVNode("p", {
                                id: _unref(subtitleId),
                                class: "text-[length:var(--size-l)] text-(--text-text-body-subtle)"
                            }, _toDisplayString(subtitle.value), 9 /* TEXT, PROPS */, _hoisted_4)
                        ])
                    ])
                ]),
                footer: _withCtx(() => [
                    _createVNode(_unref(Button), {
                        ref_key: "cancelButtonRef",
                        ref: cancelButtonRef,
                        variant: "secondary",
                        label: _ctx.$t('contact.detail.deleteContact.cancel'),
                        disabled: __props.submitting,
                        onClick: _cache[0] || (_cache[0] = ($event) => (emit('cancel')))
                    }, null, 8 /* PROPS */, ["label", "disabled"]),
                    _createVNode(_unref(Button), {
                        variant: "error",
                        "icon-left": "ri-delete-bin-line",
                        label: _ctx.$t('contact.detail.deleteContact.confirm'),
                        loading: __props.submitting,
                        disabled: __props.submitting || !__props.preview,
                        "data-testid": "delete-contact-confirm",
                        onClick: _cache[1] || (_cache[1] = ($event) => (emit('confirm')))
                    }, null, 8 /* PROPS */, ["label", "loading", "disabled"])
                ]),
                default: _withCtx(() => [
                    _createElementVNode("div", _hoisted_5, [
                        (!hasLinks.value)
                            ? (_openBlock(), _createElementBlock("p", _hoisted_6, _toDisplayString(_ctx.$t("contact.detail.deleteContact.bodyNoLinks")), 1 /* TEXT */))
                            : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                                _createElementVNode("p", _hoisted_7, _toDisplayString(_ctx.$t("contact.detail.deleteContact.linksIntro")), 1 /* TEXT */),
                                _createElementVNode("ul", _hoisted_8, [
                                    (__props.preview && __props.preview.organisations.length > 0)
                                        ? (_openBlock(), _createElementBlock("li", _hoisted_9, [
                                            _cache[4] || (_cache[4] = _createElementVNode("i", {
                                                class: "ri-building-line text-(--text-text-body-subtle) mt-0.5 shrink-0",
                                                "aria-hidden": "true"
                                            }, null, -1 /* CACHED */)),
                                            _createElementVNode("span", null, _toDisplayString(_ctx.$t("contact.detail.deleteContact.organisationsLabel", {
                                                count: __props.preview.organisations.length,
                                                names: organisationNames.value,
                                            })), 1 /* TEXT */)
                                        ]))
                                        : _createCommentVNode("v-if", true),
                                    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(__props.preview?.relations ?? [], (relation) => {
                                        return (_openBlock(), _createElementBlock("li", {
                                            key: relation.contactId,
                                            class: "flex items-start gap-2 text-[length:var(--size-m)] text-(--text-text-body)"
                                        }, [
                                            _cache[5] || (_cache[5] = _createElementVNode("i", {
                                                class: "ri-user-heart-line text-(--text-text-body-subtle) mt-0.5 shrink-0",
                                                "aria-hidden": "true"
                                            }, null, -1 /* CACHED */)),
                                            _createElementVNode("span", null, _toDisplayString(_ctx.$t("contact.detail.deleteContact.relationLabel", {
                                                name: relation.contactName,
                                                type: relationTypeLabel(relation.relationType),
                                            })), 1 /* TEXT */)
                                        ]));
                                    }), 128 /* KEYED_FRAGMENT */)),
                                    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(__props.preview?.households ?? [], (household) => {
                                        return (_openBlock(), _createElementBlock("li", {
                                            key: household.householdId,
                                            class: "flex items-start gap-2 text-[length:var(--size-m)] text-(--text-text-body)"
                                        }, [
                                            _cache[6] || (_cache[6] = _createElementVNode("i", {
                                                class: "ri-home-4-line text-(--text-text-body-subtle) mt-0.5 shrink-0",
                                                "aria-hidden": "true"
                                            }, null, -1 /* CACHED */)),
                                            _createElementVNode("span", null, [
                                                _createTextVNode(_toDisplayString(_ctx.$t("contact.detail.deleteContact.householdLabel", { name: household.name })) + " ", 1 /* TEXT */),
                                                (household.willBeDissolved)
                                                    ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                                                        _createTextVNode(_toDisplayString(_ctx.$t("contact.detail.deleteContact.householdWillBeDissolved")), 1 /* TEXT */)
                                                    ], 64 /* STABLE_FRAGMENT */))
                                                    : (household.isPrimaryContact && __props.householdNewPrincipalName)
                                                        ? (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                                                            _createTextVNode(_toDisplayString(_ctx.$t("contact.detail.deleteContact.householdNewPrincipal", { name: __props.householdNewPrincipalName })), 1 /* TEXT */)
                                                        ], 64 /* STABLE_FRAGMENT */))
                                                        : _createCommentVNode("v-if", true)
                                            ])
                                        ]));
                                    }), 128 /* KEYED_FRAGMENT */))
                                ])
                            ], 64 /* STABLE_FRAGMENT */)),
                        _createVNode(_unref(Alert), {
                            color: "warning",
                            heading: _ctx.$t('contact.detail.deleteContact.irreversibleWarning')
                        }, null, 8 /* PROPS */, ["heading"]),
                        (__props.serverError)
                            ? (_openBlock(), _createElementBlock("div", _hoisted_10, [
                                _cache[7] || (_cache[7] = _createElementVNode("i", {
                                    class: "ri-error-warning-line text-base shrink-0",
                                    "aria-hidden": "true"
                                }, null, -1 /* CACHED */)),
                                _createTextVNode(" " + _toDisplayString(__props.serverError), 1 /* TEXT */)
                            ]))
                            : _createCommentVNode("v-if", true)
                    ])
                ]),
                _: 1 /* STABLE */
            }, 8 /* PROPS */, ["modelValue", "size", "show-close-button", "dismissable", "close-on-escape", "aria-labelledby", "aria-describedby"]));
        };
    }
});
